import { useGateValue } from "@statsig/react-bindings";
import { useIntl } from "react-intl";
import { Button } from "@oai/platform/ui/Button";
import { Checkbox } from "@oai/platform/ui/Checkbox";
import { X } from "@oai/platform/ui/Icon";
import { Modal } from "@oai/platform/ui/Modal";

import PrivacyOptionsIcon from "@/assets/privacy-options-gray.svg?react";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useCookieLocation } from "@/hooks/useCookieLocation";
import { COOKIE_PERSONALIZATION_CHOICES_ENABLED_GATE } from "@/lib/cookieConsent";
import { messages } from "@/localization/Shared.messages";

function isUnitedStatesCookieLocation(countryCode?: string): boolean {
  return countryCode?.trim().toUpperCase() === "US";
}

function shouldShowYourPrivacyChoicesLabel(countryCode?: string): boolean {
  return isUnitedStatesCookieLocation(countryCode);
}

function shouldShowPrivacyOptionsIcon(countryCode?: string, regionCode?: string): boolean {
  const normalizedRegionCode = regionCode?.trim().toUpperCase();
  const subdivisionCode =
    normalizedRegionCode?.startsWith("US-") ?
      normalizedRegionCode.slice("US-".length)
    : normalizedRegionCode;
  return isUnitedStatesCookieLocation(countryCode) && subdivisionCode === "CA";
}

export function CookiePreferences() {
  const intl = useIntl();
  const includePersonalizationConsent = useGateValue(COOKIE_PERSONALIZATION_CHOICES_ENABLED_GATE);
  const { countryCode, regionCode } = useCookieLocation();
  const {
    analyticsConsentEnabled,
    marketingConsentEnabled,
    personalizationConsentEnabled,
    cookiePreferencesOpen,
    setCookiePreferencesOpen,
    updateAnalyticsConsent,
    updateMarketingConsent,
    updatePersonalizationConsent,
  } = useCookieConsent();
  const showPrivacyOptionsIcon = shouldShowPrivacyOptionsIcon(countryCode, regionCode);
  const title = intl.formatMessage(
    showPrivacyOptionsIcon ? messages.yourPrivacyChoices : messages.cookiePreferencesTitle,
  );

  return (
    <Modal open={cookiePreferencesOpen} onOpenChange={setCookiePreferencesOpen}>
      <Modal.Content className="w-[520px] max-w-[calc(100vw-2rem)]">
        <Modal.Body className="space-y-6 p-6">
          <div className="flex items-center justify-between gap-4">
            <Modal.Title className="m-0">
              <span className="inline-flex items-center gap-2">
                {title}
                {showPrivacyOptionsIcon ?
                  <PrivacyOptionsIcon
                    aria-hidden="true"
                    focusable="false"
                    className="h-[14px] w-[30px] shrink-0"
                  />
                : null}
              </span>
            </Modal.Title>
            <Modal.Close>
              <Button color="secondary" variant="ghost" uniform>
                <X />
              </Button>
            </Modal.Close>
          </div>

          <Modal.Description className="text-secondary text-sm leading-6">
            {intl.formatMessage(messages.cookiePreferencesDescription, {
              learnMore: (chunks) => (
                <a
                  href="https://openai.com/policies/cookie-policy/"
                  className="text-primary underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  {chunks}
                </a>
              ),
            })}
          </Modal.Description>

          <div className="space-y-5">
            <CookiePreferenceRow
              label={intl.formatMessage(messages.strictlyNecessaryCookies)}
              description={intl.formatMessage(messages.strictlyNecessaryCookiesDescription)}
              checked
              disabled
            />
            <CookiePreferenceRow
              label={intl.formatMessage(messages.analyticsCookies)}
              description={intl.formatMessage(messages.analyticsCookiesDescription)}
              checked={analyticsConsentEnabled}
              onCheckedChange={(checked) => updateAnalyticsConsent(checked ? "agreed" : "rejected")}
            />
            <CookiePreferenceRow
              label={intl.formatMessage(messages.marketingCookies)}
              description={intl.formatMessage(messages.marketingCookiesDescription)}
              checked={marketingConsentEnabled}
              onCheckedChange={(checked) => updateMarketingConsent(checked ? "agreed" : "rejected")}
            />
            {includePersonalizationConsent ?
              <CookiePreferenceRow
                label={intl.formatMessage(messages.personalizationCookies)}
                description={intl.formatMessage(messages.personalizationCookiesDescription)}
                checked={personalizationConsentEnabled}
                onCheckedChange={(checked) =>
                  updatePersonalizationConsent(checked ? "agreed" : "rejected")
                }
              />
            : null}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end p-6 pt-0">
          <Modal.Close>
            <Button color="primary" variant="solid" pill size="sm">
              {intl.formatMessage(messages.done)}
            </Button>
          </Modal.Close>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

export function CookiePreferencesFooterTrigger() {
  const intl = useIntl();
  const { isAuthenticated, setCookiePreferencesOpen } = useCookieConsent();
  const { countryCode, regionCode } = useCookieLocation();
  const showYourPrivacyChoicesLabel = shouldShowYourPrivacyChoicesLabel(countryCode);
  const showPrivacyOptionsIcon = shouldShowPrivacyOptionsIcon(countryCode, regionCode);

  if (isAuthenticated) {
    return null;
  }

  return (
    <button
      type="button"
      className="text-secondary group"
      onClick={() => setCookiePreferencesOpen(true)}
    >
      <span className="inline-flex items-center gap-1">
        <span className="group-hover:decoration-primary underline decoration-neutral-400 underline-offset-[0.125rem]">
          {intl.formatMessage(
            showYourPrivacyChoicesLabel ? messages.yourPrivacyChoices : messages.manageCookies,
          )}
        </span>
        {showPrivacyOptionsIcon ?
          <PrivacyOptionsIcon aria-hidden="true" focusable="false" className="h-[14px] w-[30px]" />
        : null}
      </span>
    </button>
  );
}

function CookiePreferenceRow({
  label,
  description,
  checked,
  disabled = false,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}) {
  return (
    <div className="space-y-1">
      <Checkbox
        checked={checked}
        label={label}
        disabled={disabled}
        onCheckedChange={(nextChecked) => onCheckedChange?.(nextChecked === true)}
      />
      <p className="text-secondary pl-7 text-sm leading-5">{description}</p>
    </div>
  );
}
